import { useEffect } from "react"

const FACE_DETECTOR_WASM_BASE = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm"
const FACE_DETECTOR_MODEL_URL = "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite"

let faceDetectorPromise
async function getFaceDetector() {
  if (faceDetectorPromise) return faceDetectorPromise

  faceDetectorPromise = (async () => {
    const module = await import("@mediapipe/tasks-vision")
    const { FaceDetector, FilesetResolver } = module
    const vision = await FilesetResolver.forVisionTasks(FACE_DETECTOR_WASM_BASE)
    return FaceDetector.createFromOptions(vision, {
      baseOptions: { modelAssetPath: FACE_DETECTOR_MODEL_URL },
      runningMode: "VIDEO"
    })
  })()

  // If loading fails once (network hiccup, wasm/model fetch race, etc), allow later retries.
  faceDetectorPromise.catch(() => {
    faceDetectorPromise = null
  })

  return faceDetectorPromise
}

function layoutParams(vw, vh, dw, dh, fit) {
  if (fit === "contain") {
    const scale = Math.min(dw / vw, dh / vh)
    const dx = (dw - vw * scale) / 2
    const dy = (dh - vh * scale) / 2
    return { sx: 0, sy: 0, scale, dx, dy }
  }

  const scale = Math.max(dw / vw, dh / vh)
  const sw = dw / scale
  const sh = dh / scale
  const sx = (vw - sw) / 2
  const sy = (vh - sh) / 2
  return { sx, sy, scale, dx: 0, dy: 0 }
}

function lerpBox(a, b, t) {
  if (!a) return b
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    w: a.w + (b.w - a.w) * t,
    h: a.h + (b.h - a.h) * t
  }
}

function pickStableBox(prevBox, nextBoxes) {
  if (!nextBoxes?.length) return null
  const sorted = [...nextBoxes].sort((a, b) => (b.w * b.h) - (a.w * a.h))
  const next = sorted[0]
  if (!prevBox) return next

  const prevArea = prevBox.w * prevBox.h
  const nextArea = next.w * next.h
  if (prevArea > 0 && nextArea < prevArea * 0.18) return prevBox

  const pcx = prevBox.x + prevBox.w / 2
  const pcy = prevBox.y + prevBox.h / 2
  const ncx = next.x + next.w / 2
  const ncy = next.y + next.h / 2
  const dist = Math.hypot(ncx - pcx, ncy - pcy)
  const maxJump = Math.max(prevBox.w, prevBox.h) * 1.15
  if (dist > maxJump) return prevBox

  return next
}

export function useFaceBlurOverlay({
  enabled,
  sourceVideoRef,
  overlayVideoRef,
  overlayRef,
  holdMs = 1200,
  lerpA = 0.22,
  fit = "cover"
}) {
  useEffect(() => {
    if (!enabled) {
      const overlayElRef = overlayRef || overlayVideoRef
      const overlay = overlayElRef?.current
      if (overlay) {
        if (overlay instanceof HTMLCanvasElement) {
          const ctx = overlay.getContext("2d")
          if (ctx) ctx.clearRect(0, 0, overlay.width, overlay.height)
        }
        overlay.style.opacity = "0"
        overlay.style.clipPath = "ellipse(0px 0px at 0px 0px)"
        overlay.style.webkitClipPath = "ellipse(0px 0px at 0px 0px)"
        overlay.style.maskImage = "none"
        overlay.style.webkitMaskImage = "none"
        overlay.style.filter = ""
      }
      return undefined
    }

    let stopped = false
    const video = sourceVideoRef.current
    const overlayElRef = overlayRef || overlayVideoRef
    const overlay = overlayElRef?.current
    if (!video || !overlay) return undefined
    const overlayIsCanvas = overlay instanceof HTMLCanvasElement

    const deviceMemory = navigator.deviceMemory || 0
    const cores = navigator.hardwareConcurrency || 0
    const lowEnd = (deviceMemory && deviceMemory <= 2) || (cores && cores <= 4)
    const detectIntervalMs = lowEnd ? 260 : 140

    let faceTarget = null
    let faceCurrent = null
    let pendingDetect = null
    let lastDetectAt = 0
    let lastSeenAt = 0
    let rafId = null
    let lastDrawAt = 0
    let lastFilterReadAt = 0
    let cachedBaseFilter = "none"
    let lastAppliedCanvasFilter = ""

    let lastBlurReadAt = 0
    let cachedBlurPx = 12
    function faceBlurPx(now) {
      if (now - lastBlurReadAt > 250) {
        lastBlurReadAt = now
        const raw = window.getComputedStyle(overlay).getPropertyValue("--yano-face-blur")
          || window.getComputedStyle(overlay.parentElement || overlay).getPropertyValue("--yano-face-blur")
          || ""
        const value = Number.parseFloat(raw)
        if (Number.isFinite(value)) cachedBlurPx = value
      }
      return cachedBlurPx
    }

    function baseFilter(now) {
      if (now - lastFilterReadAt > 250) {
        lastFilterReadAt = now
        try {
          cachedBaseFilter = window.getComputedStyle(video).filter || "none"
        } catch (_error) {
          cachedBaseFilter = "none"
        }
      }
      return cachedBaseFilter
    }

    function setClipFromBox(box, now) {
      if (!box) {
        overlay.style.opacity = "0"
        overlay.style.clipPath = "ellipse(0px 0px at 0px 0px)"
        overlay.style.webkitClipPath = "ellipse(0px 0px at 0px 0px)"
        overlay.style.maskImage = "none"
        overlay.style.webkitMaskImage = "none"
        return
      }

      const w = overlay.clientWidth || 1
      const h = overlay.clientHeight || 1

      const pad = faceBlurPx(now) * 0.95
      let rx = Math.max(10, box.w * 0.74 + pad)
      let ry = Math.max(10, box.h * 0.98 + pad * 1.2)

      rx = Math.min(rx, Math.max(12, w / 2 - 2))
      ry = Math.min(ry, Math.max(12, h / 2 - 2))

      let cx = box.x + box.w / 2
      let cy = box.y + box.h / 2 + box.h * 0.08
      cx = Math.min(w - rx, Math.max(rx, cx))
      cy = Math.min(h - ry, Math.max(ry, cy))

      const clip = `ellipse(${rx}px ${ry}px at ${cx}px ${cy}px)`
      overlay.style.opacity = "1"
      overlay.style.clipPath = clip
      overlay.style.webkitClipPath = clip

      const edgeMin = Math.min(cx - rx, cy - ry, (w - (cx + rx)), (h - (cy + ry)))
      if (edgeMin < 10) {
        overlay.style.maskImage = "none"
        overlay.style.webkitMaskImage = "none"
      } else {
        const innerStop = 70
        const outerStop = 86
        const mask = `radial-gradient(ellipse ${rx}px ${ry}px at ${cx}px ${cy}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) ${innerStop}%, rgba(0,0,0,0) ${outerStop}%)`
        overlay.style.maskImage = mask
        overlay.style.webkitMaskImage = mask
        overlay.style.maskRepeat = "no-repeat"
        overlay.style.webkitMaskRepeat = "no-repeat"
        overlay.style.maskSize = "100% 100%"
        overlay.style.webkitMaskSize = "100% 100%"
      }
    }

    overlay.style.opacity = "0"
    overlay.style.clipPath = "ellipse(0px 0px at 0px 0px)"
    overlay.style.webkitClipPath = "ellipse(0px 0px at 0px 0px)"
    overlay.style.maskImage = "none"
    overlay.style.webkitMaskImage = "none"

    const canvasCtx = overlayIsCanvas ? overlay.getContext("2d") : null

    async function runDetect(now) {
      try {
        const detector = await getFaceDetector()
        const result = detector.detectForVideo(video, now)
        const rect = overlay.getBoundingClientRect()
        const dw = Math.max(2, rect.width)
        const dh = Math.max(2, rect.height)
        const vw = video.videoWidth || 1280
        const vh = video.videoHeight || 720
        const layout = layoutParams(vw, vh, dw, dh, fit)

        const boxes = (result?.detections || [])
          .map((d) => d.boundingBox)
          .filter(Boolean)
          .map((b) => ({
            x: (b.originX - layout.sx) * layout.scale + layout.dx,
            y: (b.originY - layout.sy) * layout.scale + layout.dy,
            w: b.width * layout.scale,
            h: b.height * layout.scale
          }))
          .filter((b) => b.w > 10 && b.h > 10)

        const prev = faceTarget || faceCurrent
        const picked = pickStableBox(prev, boxes)

        if (picked) {
          lastSeenAt = now
          faceTarget = picked
        } else if (now - lastSeenAt > holdMs) {
          faceTarget = null
        }
      } catch (_error) {
        // Best-effort.
      }
    }

    function drawCanvas(now) {
      if (!overlayIsCanvas || !canvasCtx) return

      // No need to draw if we aren't showing the overlay.
      if (!faceCurrent && !faceTarget) {
        canvasCtx.clearRect(0, 0, overlay.width, overlay.height)
        return
      }

      const rect = overlay.getBoundingClientRect()
      const dw = Math.max(2, rect.width)
      const dh = Math.max(2, rect.height)

      const area = dw * dh
      const baseInterval = lowEnd ? 84 : 48
      const drawIntervalMs = area > 70000 ? baseInterval : area > 32000 ? Math.max(baseInterval, 90) : Math.max(baseInterval, 150)
      if (now - lastDrawAt < drawIntervalMs) return
      lastDrawAt = now

      const dpr = window.devicePixelRatio || 1
      const nextW = Math.max(2, Math.round(dw * dpr))
      const nextH = Math.max(2, Math.round(dh * dpr))
      if (overlay.width !== nextW || overlay.height !== nextH) {
        overlay.width = nextW
        overlay.height = nextH
      }

      canvasCtx.setTransform(dpr, 0, 0, dpr, 0, 0)
      canvasCtx.clearRect(0, 0, dw, dh)
      canvasCtx.imageSmoothingEnabled = true
      try {
        canvasCtx.imageSmoothingQuality = "high"
      } catch (_error) {}

      const vw = video.videoWidth || 1280
      const vh = video.videoHeight || 720
      const layout = layoutParams(vw, vh, dw, dh, fit)

      const blurPx = faceBlurPx(now)
      const vf = baseFilter(now)
      const extra = `blur(${blurPx}px) saturate(0.98) contrast(1.04)`
      const composedFilter = (vf && vf !== "none") ? `${vf} ${extra}` : extra

      // Important: Canvas2D `ctx.filter` does not match CSS filters consistently across browsers
      // (hue-rotate/contrast/saturate order, partial support, etc). For "product-y" looks we
      // want the same CSS filter pipeline as the base video, so we apply filters via CSS.
      canvasCtx.filter = "none"
      if (composedFilter !== lastAppliedCanvasFilter) {
        lastAppliedCanvasFilter = composedFilter
        overlay.style.filter = composedFilter
      }

      if (fit === "contain") {
        canvasCtx.drawImage(video, 0, 0, vw, vh, layout.dx, layout.dy, vw * layout.scale, vh * layout.scale)
      } else {
        const sw = dw / layout.scale
        const sh = dh / layout.scale
        canvasCtx.drawImage(video, layout.sx, layout.sy, sw, sh, 0, 0, dw, dh)
      }
    }

    function tick(now) {
      if (stopped) return

      if (video.readyState >= 2 && video.videoWidth && video.videoHeight) {
        const w = overlay.clientWidth || 0
        const h = overlay.clientHeight || 0

        // Always allow face blur, even in tiny PIP tiles, but throttle detection based on area.
        const area = w * h
        const dynamicIntervalMs = area > 70000 ? detectIntervalMs : area > 32000 ? Math.max(detectIntervalMs, 220) : Math.max(detectIntervalMs, 520)

        if (now - lastDetectAt > dynamicIntervalMs && !pendingDetect) {
          lastDetectAt = now
          pendingDetect = runDetect(now).finally(() => {
            pendingDetect = null
          })
        }

        if (faceTarget) {
          faceCurrent = lerpBox(faceCurrent, faceTarget, lerpA)
        } else if (now - lastSeenAt > holdMs) {
          faceCurrent = null
        }

        if (now - lastSeenAt > holdMs) {
          faceTarget = null
          faceCurrent = null
          setClipFromBox(null, now)
        } else {
          setClipFromBox(faceCurrent, now)
          drawCanvas(now)
        }
      }

      rafId = window.requestAnimationFrame(tick)
    }

    rafId = window.requestAnimationFrame(tick)

    return () => {
      stopped = true
      if (rafId) window.cancelAnimationFrame(rafId)
      rafId = null
      faceTarget = null
      faceCurrent = null
      pendingDetect = null
      if (overlay) {
        if (overlay instanceof HTMLCanvasElement) {
          const ctx = overlay.getContext("2d")
          if (ctx) ctx.clearRect(0, 0, overlay.width, overlay.height)
        }
        overlay.style.opacity = "0"
        overlay.style.clipPath = "ellipse(0px 0px at 0px 0px)"
        overlay.style.webkitClipPath = "ellipse(0px 0px at 0px 0px)"
        overlay.style.maskImage = "none"
        overlay.style.webkitMaskImage = "none"
        overlay.style.filter = ""
      }
    }
  }, [enabled, sourceVideoRef, overlayVideoRef, overlayRef, holdMs, lerpA, fit])
}
